import React, { useRef, useState } from "react";
import { Button, Dropdown, Grid, Icon } from "semantic-ui-react";
import { getDropdownOptions, guid, handleAddressValue, handleGetValueFromId, handleInputType, handleNestedValidation } from "../../../Helpers";
import { getIsWhiteLabeled } from "../../../helpers/PermissionHelper";
import { BrokerConstants } from "../../constants/BrokerConstants";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import BCConfirmationModal from "../../ui/BCConfirmationModal";
import BCEmptyState from "../../ui/BCEmptyState";
import BCFormInput from "../../ui/BCFormInput";
import BCTable from "../../ui/BCTable";
import BCValidationMessage from "../../ui/BCValidationMessage";
import { BPPropertyUnitsTable } from "../BPApplicationTables";
import { BPQuestionLabels } from "../BPQuestionLabels";
import BPLoanDetailSection from "./BPLoanDetailSection";

interface IBPLoanPropertiesContentProps {
  applicationMetadata: any;
  brokerColor: any;
  brokerButtonOevrrideCSS: any;
  brokerIconColorOverride: any;
  invalidProps: any;
  isEditMode: boolean;
  accordionProps: any;
  isLoanInfo: any;
  loanProgramId?: string;
  loanInfo: any;
  propertiesCopy: any;
  propertiesPatchArray: any;
  setPropertiesPatchArray: any;
  handleDeletePropertyAsync: any;
  handleUnitDeleteAsync: any;
  newLoanUnits: any;
  setNewLoanUnits: any;
  setInteriorCardSuccessMessage: any;
  handleAddNewProperty: any;
  newPropertyFocusRef?: any;
  setInvalidProps: any;
  errorPropertyIds?: Set<string>;
}

const BPLoanPropertiesContent = ({...props}: IBPLoanPropertiesContentProps) => {
  const [selectedPropertyId, setSelectedPropertyId] = useState("" as string);
  const [checkedUnits, setCheckedUnits] = useState([] as string[]);
  const [openConfirmDeletePropertyModal, setOpenConfirmDeletePropertyModal] = useState(false as boolean);
  const [openConfirmDeleteUnitModal, setOpenConfirmDeleteUnitModal] = useState(false as boolean);
  const [isUnitBulkEditModalOpen, setIsUnitBulkEditModalOpen] = useState(false as boolean);
  const unitRowRef = useRef([] as any[]);
  const noUnitLoanType = props.loanProgramId && [GlobalConstants.LoanProgramIds.DEVELOPMENT, GlobalConstants.LoanProgramIds.BRIDGE].indexOf(props.loanProgramId) > -1;
  const isPropertyEllipsesDisplay = FeatureFlags.IS_UNIT_PROPERTY_REDESIGN && props.isEditMode && (!noUnitLoanType && props.loanProgramId !== GlobalConstants.LoanProgramIds.FIX_AND_FLIP);
  const isConstructionScheduleDisplay = FeatureFlags.IS_UNIT_PROPERTY_REDESIGN && !noUnitLoanType;
  const propertyList = props.propertiesCopy.filter(property => property.collateralProperty === false).map((property) =>{
    if (!props.isLoanInfo) {
      property["loanPropertyUnitReconveyances"] = [...property.borrowerApplicationPropertyUnits];
    }

    return property;
  });

  const collateralPropertyList = props.propertiesCopy.filter(property => property.collateralProperty);

  const handleDisplayValue = (obj, field) => {
    const selectedObj = props.propertiesPatchArray ? props.propertiesPatchArray.find(p => p.id === obj.id) : undefined;
    if (selectedObj && Object.keys(selectedObj).includes(field)) {
      return selectedObj[field];
    }

    return obj[field];
  };

  const handleDisplayPropertyValueFromUnit = (property, field) => {
    const propertyUnit = (property.loanPropertyUnitReconveyances && property.loanPropertyUnitReconveyances[0]) ? property.loanPropertyUnitReconveyances[0] : null;
    const selectedUnit = propertyUnit ? props.propertiesPatchArray.find(x => x.id === propertyUnit.id) : null;
    if (selectedUnit && Object.keys(selectedUnit).includes(field)) {
      return selectedUnit[field];
    }

    if (propertyUnit && Object.keys(propertyUnit).includes(field)) {
      return propertyUnit[field];
    }

    return null;
  };

  const handleDisplayUnitValue = (property, unit, field) => {
    const selectedProperty = props.propertiesPatchArray ? props.propertiesPatchArray.find(p => p.id === property.id) : undefined;
    if (selectedProperty && selectedProperty.loanPropertyUnitReconveyances && Array.isArray(selectedProperty.loanPropertyUnitReconveyances)) {
      const selectedUnit = selectedProperty.loanPropertyUnitReconveyances.find(u => u.id === unit.id);
      if (selectedUnit && Object.keys(selectedUnit).includes(field))
        return selectedUnit[field];
    }

    return unit[field];
  };

  const handlePropertyLevelUnitFormChange = (value, field, property, patchArray, setPatchArray) => {
    const formattedValue = handleInputType(GlobalConstants.InputTypes.INT, value, undefined, undefined, 50);

    if (property.loanPropertyUnitReconveyances && property.loanPropertyUnitReconveyances[0]) {
      property.loanPropertyUnitReconveyances[0][field] = formattedValue;

      
      const selectedProperty = patchArray.find(x => x.id === property.id);
      const newUnit = [
        {
          "id": property.loanPropertyUnitReconveyances[0].id,
          "loanPropertyId": property.id,
        }
      ];

      newUnit[0][field] = formattedValue;
      if (selectedProperty) {
        if (selectedProperty.loanPropertyUnitReconveyances && selectedProperty.loanPropertyUnitReconveyances[0]) {
          selectedProperty.loanPropertyUnitReconveyances[0][field] = formattedValue;
        }
        else {
          selectedProperty["loanPropertyUnitReconveyances"] = newUnit;
        }

        setPatchArray([...patchArray]);
      }
      else {
        const editProperty = {
          "id": property.id,
          "loanPropertyUnitReconveyances": newUnit,
        };

        setPatchArray([{...editProperty}]);
      }
    }

  };

  const handlePropertyTitle = (property) => {
    return `${BrokerConstants.InteriorCardAccordions.Properties.name}: ${handleAddressValue({
      address: handleDisplayValue(property, "street"),
      city: handleDisplayValue(property, "cityName"),
      state: handleDisplayValue(property, "stateName"),
      zip: handleDisplayValue(property, "zip"),
    })}`;
  };

  const handleSubAccodionSections = (property) => {
    const propertyDetail = [
      {
        sectionTitle: "Property Details",
        inputs: [
          {
            value: handleDisplayValue(property, "street"),
            label: "Address (or unique identifier):",
            size: 4,
            field: "street",
            required: true,
            readonly: handleDisplayValue(property, "street") === "Development"
          },
          {
            value: {"cityName": property.cityName, "stateName": property.stateName, "zip": property.zip},
            address: true,
            defaultValueObj: {...property},
            placeDetailsObject: {id: property.id},
            setPlaceDetailsObject: handlePropertyAddressChange,
            required: true,
            fieldsNameOverridesArray: [
              {
                addressComponent: "city",
                field: "cityName"
              },
              {
                addressComponent: "state",
                field: "stateName"
              },
              {
                addressComponent: "county",
                field: "countyName"
              },
              {
                addressComponent: "zip",
                field: "zip"
              },
            ],
            label: "Zip:",
            size: 4
          },
          {
            value: handleDisplayValue(property, "newStructurePropertyTypeId") === GlobalConstants.PropertyTypeIds.APARTMENT ? handleDisplayValue(property, "numberOfUnits") : property.loanPropertyUnitReconveyances.length,
            label: "No. of Units:",
            size: 4,
            field: "numberOfUnits",
            readonly: handleDisplayValue(property, "newStructurePropertyTypeId") !== GlobalConstants.PropertyTypeIds.APARTMENT,
            required: true,
            condition: !noUnitLoanType
          },
          {
            value: handleDisplayValue(property, "existingStructurePropertyTypeId"),
            label: "Existing Property Type:",
            size: 4,
            field: "existingStructurePropertyTypeId",
            dropdown: getDropdownOptions(props.applicationMetadata.propertyTypes, ["id"], "name"),
            required: true,
            readonly: props.loanProgramId === GlobalConstants.LoanProgramIds.FIX_AND_FLIP
          },
          {
            value: handleDisplayValue(property, "newStructurePropertyTypeId"),
            label: "New Property Type:",
            size: 4,
            field: "newStructurePropertyTypeId",
            dropdown: getDropdownOptions(props.applicationMetadata.propertyTypes, ["id"], "name"),
            required: true,
            readonly: !property.isNewProperty,
          },
          {
            value: handleDisplayPropertyValueFromUnit(property, "estimatedSalesPrice"),
            handleCustomFormChange: handlePropertyLevelUnitFormChange,
            label: "Finished Value (i.e. ARV):",
            size: 4,
            field: "estimatedSalesPrice",
            required: true,
            condition: props.loanProgramId === GlobalConstants.LoanProgramIds.BRIDGE
          },
          {
            value: handleDisplayPropertyValueFromUnit(property, "estimatedSalesPrice"),
            handleCustomFormChange: handlePropertyLevelUnitFormChange,
            label: BPQuestionLabels.EstimatedSalesPrice,
            size: 4,
            field: "estimatedSalesPrice",
            required: true,
            condition: property.newStructurePropertyTypeId === GlobalConstants.PropertyTypeIds.APARTMENT
          },
          {
            value: handleDisplayValue(property, "parcelNumber"),
            label: "Current Parcel No.:",
            size: 4,
            field: "parcelNumber",
            required: !(handleDisplayValue(property, "newStructurePropertyTypeId") === GlobalConstants.PropertyTypeIds.LAND_UNDEVELOPED || handleDisplayValue(property, "newStructurePropertyTypeId") === GlobalConstants.PropertyTypeIds.NO_STRUCTURE_LAND),
          },
          {
            value: handleDisplayValue(property, "lotNumber"),
            label: "Lot No.:",
            size: 4,
            field: "lotNumber",
          },
          {
            value: handleDisplayValue(property, "zoning"),
            label: "Zoning:",
            size: 4,
            field: "zoning",
          },
          {
            value: handleDisplayValue(property, "workStageId"),
            label: "% of Work Complete:",
            size: 4,
            field: "workStageId",
            dropdown: getDropdownOptions(props.applicationMetadata.workStages, ["id"], "name"),
          },
        ]
      }
    ];

    if (!isConstructionScheduleDisplay) {
      return propertyDetail;
    }

    return [...propertyDetail, {
      sectionTitle: "Property Schedule",
      inputs: [
        {
          value: handleDisplayValue(property, "monthOfFirstDraw"),
          label: "Month of First Draw",
          size: 4,
          field: "monthOfFirstDraw",
          inputType: GlobalConstants.InputTypes.INT,
          required: true
        },
        {
          value: handleDisplayValue(property, "buildTimeInMonths"),
          label: "Months to Finish Construction",
          size: 4,
          field: "buildTimeInMonths",
          inputType: GlobalConstants.InputTypes.INT,
          required: true
        },
        {
          value: handleDisplayValue(property, "timeFrameToSellOrRefinanceInMonths"),
          label: "Month to Sell or Refinance",
          size: 4,
          field: "timeFrameToSellOrRefinanceInMonths",
          inputType: GlobalConstants.InputTypes.INT,
          required: true
        }
      ]
    }];
  };

  const handleUnitInfoChanges = (value, field, unit, type, property) => {
    const formattedValue = handleInputType(type, value, undefined, undefined, 50);
    unit[field] = formattedValue;

    const selectedNewUnit = props.newLoanUnits.find(x => x.id === unit.id);
    if (selectedNewUnit) {
      selectedNewUnit[field] = formattedValue;
      props.setNewLoanUnits([...props.newLoanUnits]);
      return;
    }

    const selectedProperty = props.propertiesPatchArray.find(p => p.id === property.id);
    const updatedUnit = {
      id: unit.id,
      loanPropertyId: property.id
    };

    updatedUnit[field] = formattedValue;
    if (selectedProperty && selectedProperty.loanPropertyUnitReconveyances) {
      const selectedUnit = selectedProperty.loanPropertyUnitReconveyances.find(u => u.id === unit.id);
      if (selectedUnit) {
        selectedUnit[field] = formattedValue;
      }
      else {
        selectedProperty.loanPropertyUnitReconveyances.push({...updatedUnit});
      }
    }
    else if (selectedProperty && !selectedProperty.loanPropertyUnitReconveyances) {
      selectedProperty["loanPropertyUnitReconveyances"] = [{...updatedUnit}];
    }
    else {
      const updatedproperty = {
        id: property.id,
        loanPropertyUnitReconveyances: [{...updatedUnit}]
      };

      props.propertiesPatchArray.push({...updatedproperty});
    }

    props.setPropertiesPatchArray([...props.propertiesPatchArray]);
  };

  const handlePropertyAddressChange = (placeObjInputValue) => {
    const editDisplayProperty = props.propertiesCopy.find(x => x.id === placeObjInputValue.id);
    const editPatchProperty = props.propertiesPatchArray.find(x => x.id === placeObjInputValue.id);
    if (editDisplayProperty) {
      for(const key in placeObjInputValue) {
        editDisplayProperty[key] = placeObjInputValue[key];
      }
    }

    if (editPatchProperty) {
      for(const key in placeObjInputValue) {
        editPatchProperty[key] = placeObjInputValue[key];
      }
    }
    else {
      props.propertiesPatchArray.push({...placeObjInputValue});
    }

    props.setPropertiesPatchArray([...props.propertiesPatchArray]);
  };

  const formatLoanPropertyUnit = (units, property) => {
    const sortedUnits = units.sort((a, b) => a.sortOrder - b.sortOrder);
    const formattedApplicationProperties = [] as any[];
    sortedUnits.forEach((unit) => {
      if (FeatureFlags.IS_UNIT_PROPERTY_REDESIGN) {
        formattedApplicationProperties.push({
          "id": unit.id,
          "Unit No.": <React.Fragment>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              value={handleDisplayUnitValue(property, unit, "unitNumber")}
              field="unitNumber"
              placeholder="1"
              inputType="text"
              handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.STRING, property)}
              disabled={!props.isEditMode}
            />
            <BCValidationMessage invalidProps={props.invalidProps} prop={`property-unit-${unit.id}-unitNumber`} />
            {!props.invalidProps.includes(`property-unit-${unit.id}-unitNumber`) && <BCValidationMessage invalidProps={props.invalidProps} prop={`custom-unit-duplicated-${unit.id}`} propText="Unit number must be unique." />}
          </React.Fragment>,
          "Finished Value (i.e. ARV)": <React.Fragment>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              placeholder="10000"
              fieldType="dollar"
              inputType="number"
              value={handleDisplayUnitValue(property, unit, "estimatedSalesPrice")}
              field="estimatedSalesPrice"
              handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.DOLLAR, property)}
              disabled={!props.isEditMode}
            />
            <BCValidationMessage invalidProps={props.invalidProps} prop={`property-unit-${unit.id}-estimatedSalesPrice`} />
          </React.Fragment>,
          "No. of Bedrooms": <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            value={handleDisplayUnitValue(property, unit, "numberOfBedroom")}
            field="numberOfBedroom"
            placeholder="1"
            inputType={GlobalConstants.InputTypes.INT}
            handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.INT, property)}
            disabled={!props.isEditMode}
          />,
          "No. of Bathrooms": <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            value={handleDisplayUnitValue(property, unit, "numberOfBathroom")}
            field="numberOfBathroom"
            placeholder="1"
            inputType={GlobalConstants.InputTypes.INT}
            handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.INT, property)}
            disabled={!props.isEditMode}
          />,
          "No. of Garage Bays": <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            value={handleDisplayUnitValue(property, unit, "numberOfCarGarage")}
            field="numberOfCarGarage"
            placeholder="1"
            inputType={GlobalConstants.InputTypes.INT}
            handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.INT, property)}
            disabled={!props.isEditMode}
          />,
          "Square Footage": <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            value={handleDisplayUnitValue(property, unit, "squareFootage")}
            field="squareFootage"
            placeholder="1"
            inputType={GlobalConstants.InputTypes.INT}
            handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.INT, property)}
            disabled={!props.isEditMode}
          />
        });
      }
      else {
        formattedApplicationProperties.push({
          "id": unit.id,
          "Unit": <React.Fragment>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              value={handleDisplayUnitValue(property, unit, "unitNumber")}
              field="unitNumber"
              placeholder="1"
              inputType="text"
              handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.STRING, property)}
              disabled={!props.isEditMode}
            />
            <BCValidationMessage invalidProps={props.invalidProps} prop={`property-unit-${unit.id}-unitNumber`} />
            {!props.invalidProps.includes(`property-unit-${unit.id}-unitNumber`) && <BCValidationMessage invalidProps={props.invalidProps} prop={`property-unit-${unit.id}-unitNumber-duplicate`} propText="Unit number must be unique." />}
          </React.Fragment>,
          "Current Value (AIV)": <React.Fragment>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              placeholder="10000"
              fieldType="dollar"
              inputType="number"
              value={handleDisplayUnitValue(property, unit, "asIsValue")}
              field="asIsValue"
              handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.DOLLAR, property)}
              disabled={!props.isEditMode}
            />
            <BCValidationMessage invalidProps={props.invalidProps} prop={`property-unit-${unit.id}-asIsValue`} />
          </React.Fragment>,
          "Completed Value (ARV)": <React.Fragment>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              placeholder="10000"
              fieldType="dollar"
              inputType="number"
              value={handleDisplayUnitValue(property, unit, "estimatedSalesPrice")}
              field="estimatedSalesPrice"
              handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.DOLLAR, property)}
              disabled={!props.isEditMode}
            />
            <BCValidationMessage invalidProps={props.invalidProps} prop={`property-unit-${unit.id}-estimatedSalesPrice`} />
          </React.Fragment>,
          "Bedrooms": <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            value={handleDisplayUnitValue(property, unit, "numberOfBedroom")}
            field="numberOfBedroom"
            placeholder="1"
            inputType={GlobalConstants.InputTypes.INT}
            handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.INT, property)}
            disabled={!props.isEditMode}
          />,
          "Bathrooms": <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            value={handleDisplayUnitValue(property, unit, "numberOfBathroom")}
            field="numberOfBathroom"
            placeholder="1"
            inputType={GlobalConstants.InputTypes.INT}
            handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.INT, property)}
            disabled={!props.isEditMode}
          />,
          "Car Garage": <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            value={handleDisplayUnitValue(property, unit, "numberOfCarGarage")}
            field="numberOfCarGarage"
            placeholder="1"
            inputType={GlobalConstants.InputTypes.INT}
            handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.INT, property)}
            disabled={!props.isEditMode}
          />,
          "Square Feet": <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            value={handleDisplayUnitValue(property, unit, "squareFootage")}
            field="squareFootage"
            placeholder="1"
            inputType={GlobalConstants.InputTypes.INT}
            handleFormChange={(value, field) => handleUnitInfoChanges(value, field, unit, GlobalConstants.InputTypes.INT, property)}
            disabled={!props.isEditMode}
          />
        });
      }
    });

    return formattedApplicationProperties;
  };

  const formatLoanCollateralProperties = (loanProperties) => {
    const formattedCollateralProperties = [] as any[];
    loanProperties.forEach((loanProperty) => {
      if (loanProperty.loanPropertyUnitReconveyances) {
        loanProperty.loanPropertyUnitReconveyances.forEach((loanPropertyUnit) => {
          formattedCollateralProperties.push({
            "Bank": <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              value={loanProperty.mortgageHolder1}
              placeholder="Bank"
              inputType="text"
              disabled={true}
            />,
            "Mortgage": <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              value={loanProperty.mortgageAmount1}
              placeholder="100000"
              inputType="text"
              disabled={true}
            />,
            "Status": <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              value={handleGetValueFromId(props.applicationMetadata.reoStatuses, loanProperty.reoStatusId)}
              dropdownOptions={getDropdownOptions(Array.from(props.applicationMetadata.propertyTypes as any[], x => x.name))}
              inputType="text"
              disabled={true}
            />,
            "Address": <BCFormInput
              type={GlobalConstants.FormInputTypes.PLACES}
              disabled={true}
              placeholder="1234 Main Street, Seattle, WA 98052, USA"
              defaultValue={handleAddressValue({
                address: loanProperty.street,
                city: loanProperty.cityName,
                state: loanProperty.stateName,
                zip: loanProperty.zip
              })}
              placeFields={["address", "city", "state", "county", "zip"]}
              placeFieldsNameOverrides={[
                {
                  addressComponent: "address",
                  field: "street"
                },
                {
                  addressComponent: "city",
                  field: "cityName"
                },
                {
                  addressComponent: "state",
                  field: "stateName"
                },
                {
                  addressComponent: "county",
                  field: "countyName"
                },
                {
                  addressComponent: "zip",
                  field: "zip"
                },
              ]}
            />,
            "Market Value": <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              placeholder="10000"
              fieldType="dollar"
              inputType="number"
              value={loanPropertyUnit.asIsValue}
              disabled={true}
            />
          });
        });
      }
    });
    return formattedCollateralProperties;
  };

  const handleDeletePropertyAsync = async () => {
    let isApiSuccess = false;
    setOpenConfirmDeletePropertyModal(false);
    await props.handleDeletePropertyAsync(selectedPropertyId).then(() => isApiSuccess = true);
    if (isApiSuccess) {
      props.setInteriorCardSuccessMessage("Property has been deleted");
      setTimeout(() => {
        props.setInteriorCardSuccessMessage("");
      }, 5000);
      setSelectedPropertyId("");
    }
  };

  const handleAddNewUnit = (property) => {
    const unitId = guid();
    let newUnit = {} as any;
    newUnit = { 
      "id": unitId, 
      "unitNumber": property.loanPropertyUnitReconveyances ? (property.loanPropertyUnitReconveyances.length + 1).toString() : "1",
      "sortOrder": property.loanPropertyUnitReconveyances && property.loanPropertyUnitReconveyances.length,
      "loanPropertyId": property.id,
    };

    props.propertiesCopy.find(x => {
      if (x.id === property.id) {
        x.loanPropertyUnitReconveyances?.push(newUnit);
        x.numberOfUnits += 1;
      }
    });

    const editedProperty = props.propertiesPatchArray.find(x => x.id === property.id);
    if (editedProperty) {
      if (!editedProperty.isNewProperty) {
        props.setNewLoanUnits((otherNewUnits) => [...otherNewUnits, newUnit]);
      }
      
      editedProperty.numberOfUnits = property.loanPropertyUnitReconveyances ? property.loanPropertyUnitReconveyances.length : 1;
    }
    else {
      props.propertiesPatchArray.push({"id" : property.id, "numberOfUnits": property.numberOfUnits});
      props.setNewLoanUnits((otherNewUnits) => [...otherNewUnits, newUnit]);
    }
    
    props.setPropertiesPatchArray([...props.propertiesPatchArray]);

    const notValidUnitProps = handleNestedValidation(property.loanPropertyUnitReconveyances, ["estimatedSalesPrice", "unitNumber"], "custom-unit");
    if (notValidUnitProps.length > 0) {
      setTimeout(() => {
        props.setInvalidProps(notValidUnitProps);
      }, 50);
    }
  };

  const handleDeleteUnitsAsync = async () => {
    let isApiSuccess = false;
    setOpenConfirmDeleteUnitModal(false);
    await Promise.all(checkedUnits.map(async (unit) => props.handleUnitDeleteAsync(unit))).then(() => isApiSuccess = true);
    if (isApiSuccess) {
      props.setInteriorCardSuccessMessage((checkedUnits && checkedUnits.length > 0) ? "Units have been deleted" : "Unit has been deleted");
      setTimeout(() => {
        props.setInteriorCardSuccessMessage("");
      }, 5000);
      setCheckedUnits([]);
    }
  };

  return (
    <React.Fragment>
      {selectedPropertyId && <BCConfirmationModal
        isModalOpen={openConfirmDeletePropertyModal}
        setIsModalOpen={setOpenConfirmDeletePropertyModal}
        modalHeader="Warning, you will lose data!"
        modalContent="Are you sure you want to delete this property? This action cannot be undone"
        handleConfirm={() => handleDeletePropertyAsync()}
        confirmButtonWord="Delete"
        isWarning
      />}
      {(checkedUnits && checkedUnits.length > 0) && <React.Fragment>
        <BCConfirmationModal
          isModalOpen={openConfirmDeleteUnitModal}
          setIsModalOpen={setOpenConfirmDeleteUnitModal}
          modalHeader="Warning, you will lose data!"
          modalContent={`Are you sure you want to delete ${checkedUnits.length} ${checkedUnits.length === 1 ? "unit" : "units"}? This action cannot be undone`}
          handleConfirm={() => handleDeleteUnitsAsync()}
          confirmButtonWord="Delete"
          isWarning
        />
      </React.Fragment>}
      {propertyList.length > 0 ? propertyList.map((property) => {
        return (
          <BPLoanDetailSection
            title={handlePropertyTitle(property)}
            titleError={(props.errorPropertyIds instanceof Set) && props.errorPropertyIds.has(property.id)}
            titleEllipsisItems={(isPropertyEllipsesDisplay && property.street !== "Development") && <Dropdown key={`ellipsis-dropdown-${property.id}`} icon="ellipsis horizontal" pointing='right'>
                <Dropdown.Menu key={"ellipsis-menu"}>
                <Dropdown.Item key={`title-ellipsis-${property.id}-delete`} disabled={!props.isEditMode} onClick={() => {setOpenConfirmDeletePropertyModal(true); setSelectedPropertyId(property.id);}}>Delete Property</Dropdown.Item>
                <Dropdown.Item key={`title-ellipsis-${property.id}-copy`} disabled={!props.isEditMode} onClick={() => props.handleAddNewProperty(property)}>Copy Property</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>}
            accordionId={`${property.id}`}
            key={`${BrokerConstants.InteriorCardAccordions.Properties.id}-${property.id}`}
            displayObj={property}
            mainPatchArr={props.propertiesPatchArray}
            setMainPatchArr={props.setPropertiesPatchArray}
            subPatchObj={{id: property.id}}
            setSubPatchObj={props.setPropertiesPatchArray}
            isEditMode={props.isEditMode}
            {...props.accordionProps}
            validationGroupName={`property-${property.id}`}
            invalidProps={props.invalidProps}
            inputs={!FeatureFlags.IS_UNIT_PROPERTY_REDESIGN && [
              {
                value: handleDisplayValue(property, "existingStructurePropertyTypeId"),
                label: "Existing Property Type:",
                size: 4,
                field: "existingStructurePropertyTypeId",
                dropdown: getDropdownOptions(props.applicationMetadata.propertyTypes, ["id"], "name"),
              },
              {
                value: handleDisplayValue(property, "newStructurePropertyTypeId"),
                label: "New Property Type:",
                size: 4,
                field: "newStructurePropertyTypeId",
                dropdown: getDropdownOptions(props.applicationMetadata.propertyTypes, ["id"], "name"),
                readonly: true,
                required: true,
              },
              {
                value: handleDisplayValue(property, "street"),
                label: "Street:",
                size: 4,
                field: "street",
                required: true
              },
              {
                value: {"cityName": property.cityName, "stateName": property.stateName, "zip": property.zip},
                address: true,
                defaultValueObj: {...property},
                placeDetailsObject: {id: property.id},
                setPlaceDetailsObject: handlePropertyAddressChange,
                required: true,
                fieldsNameOverridesArray: [
                  {
                    addressComponent: "city",
                    field: "cityName"
                  },
                  {
                    addressComponent: "state",
                    field: "stateName"
                  },
                  {
                    addressComponent: "county",
                    field: "countyName"
                  },
                  {
                    addressComponent: "zip",
                    field: "zip"
                  },
                ],
                label: "Zip:",
                size: 4
              }
            ]}
            inputSections={FeatureFlags.IS_UNIT_PROPERTY_REDESIGN ? handleSubAccodionSections(property) : []}
            table={(FeatureFlags.IS_UNIT_PROPERTY_REDESIGN && property.newStructurePropertyTypeId !== GlobalConstants.PropertyTypeIds.APARTMENT && !noUnitLoanType) ? 
              <div className="summary-subaccordion">
              <div className="summary-subaccordion-title">
                <label key={`sub-accordion-${property.id}`}>Units</label>
              </div>
              {props.isEditMode && <div className="interior-card-unit-buttons">
                <Button
                  className={getIsWhiteLabeled() ? "interior-card-button" : "bp-button interior-card-button"}
                  icon
                  basic
                  onClick={() => setIsUnitBulkEditModalOpen(true)}
                  disabled={!props.isEditMode || checkedUnits.length < 1}
                  >
                  <div className="gmr-button-container">
                    <Icon name='pencil' size="large" />
                    <div className="gmr-button-icon-text">Edit</div>
                  </div>
                </Button>
                <Button
                  className={getIsWhiteLabeled() ? "interior-card-button" : "bp-button interior-card-button"}
                  icon
                  basic
                  onClick={() => setOpenConfirmDeleteUnitModal(true)}
                  disabled={!props.isEditMode 
                    || (property.loanPropertyUnitReconveyances && property.loanPropertyUnitReconveyances.length <= 1) 
                    || checkedUnits.length < 1 
                    || checkedUnits.length === property.loanPropertyUnitReconveyances.length}
                  >
                  <div className="gmr-button-container">
                    <Icon name='trash alternate outline' size="large" />
                    <div className="gmr-button-icon-text">Delete</div>
                  </div>
                </Button>
                <Button
                  className={getIsWhiteLabeled() ? "interior-card-button" : "bp-button interior-card-button"}
                  icon
                  basic
                  onClick={() => handleAddNewUnit(property)}
                  disabled={!props.isEditMode}
                  >
                  <div className="gmr-button-container">
                    <Icon name='add' size="large" />
                    <div className="gmr-button-icon-text">Add Unit</div>
                  </div>
                </Button>
              </div>}
              <BPPropertyUnitsTable
                isloan={true}
                unitRef={null}
                unitRowRef={unitRowRef}
                handleUnitValue={handleDisplayUnitValue} 
                properties={[property]}
                info={props.loanInfo}
                setInfo={props.setPropertiesPatchArray}
                checkedUnits={checkedUnits}
                setCheckedUnits={setCheckedUnits}
                isUnitBulkEditModalOpen={isUnitBulkEditModalOpen}
                setIsUnitBulkEditModalOpen={setIsUnitBulkEditModalOpen}
                brokerColor={props.brokerColor}
                handleUnitFormChange={handleUnitInfoChanges}
                disabled={!props.isEditMode}
                invalidProps={props.invalidProps}
              />
            </div>
              : (FeatureFlags.IS_UNIT_PROPERTY_REDESIGN && (noUnitLoanType || property.newStructurePropertyTypeId === GlobalConstants.PropertyTypeIds.APARTMENT)) ? <React.Fragment></React.Fragment>
              : <Grid><Grid.Row className="details-table-row"><Grid.Column width={16} stretched>
                <BCTable
                  data={formatLoanPropertyUnit(property.loanPropertyUnitReconveyances, property)}
                  dataRules={[{ field: "id", rule: "hide" }]}
                  thin
                  dataSingleLine
                  overflowX
                  unstackable
                  brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
                  columns={11}
                  addRow
                  editMode={props.isEditMode}
                />
              </Grid.Column>
            </Grid.Row>
            </Grid>}
          />);}) 
        : <Grid>
          <Grid.Row className="details-table-row">
            <Grid.Column width={16}>
              <BCEmptyState
                emptyStateClass="application-details-empty-state"
                icon="home"
                brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
                className={getIsWhiteLabeled() ? props.brokerIconColorOverride : ""}
                text="Looks like you don’t have any properties. Click here to edit your application."
                // TODO: uncomment to enable add property - PBI 5400
                // button={<Button className={getIsWhiteLabeled() ? `primary-under-table-button ${props.brokerButtonOevrrideCSS}` : "bp-button primary-under-table-button"} icon onClick={() => handlePropertyAdd()}>Add Property</Button>}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>}
      {!FeatureFlags.IS_BETA &&
        <BPLoanDetailSection
          title={BrokerConstants.InteriorCardAccordions.CollateralProperties.name}
          accordionId={BrokerConstants.InteriorCardAccordions.CollateralProperties.id}
          subPatchObj={props.propertiesPatchArray}
          setSubPatchObj={props.setPropertiesPatchArray}
          isEditMode={false}
          {...props.accordionProps}
          table={formatLoanCollateralProperties(collateralPropertyList).length > 0 ? <Grid><Grid.Row className="details-table-row">
            <Grid.Column width={16} stretched>
              <BCTable
                data={formatLoanCollateralProperties(collateralPropertyList)}
                thin
                dataSingleLine
                overflowX
                unstackable
                brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
                columns={11}
              />
            </Grid.Column>
          </Grid.Row>
          </Grid>
            : <Grid>
              <Grid.Row className="details-table-row">
                <Grid.Column width={16}>
                  <BCEmptyState
                    emptyStateClass="application-details-empty-state"
                    icon="home"
                    brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
                    className={getIsWhiteLabeled() ? props.brokerIconColorOverride : ""}
                    text="Looks like you don’t have any collateral properties."
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>}
        />
      }    
      <div id="new-property-focus" ref={props.newPropertyFocusRef as any} />
    </React.Fragment>
  );
};

export default BPLoanPropertiesContent;